import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Megaphone, Loader2, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

export default function CircularsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: "", body: "", audience: "all" });

  async function load() {
    const { data } = await api.get("/circulars");
    setItems(data);
  }
  useEffect(() => { load().catch(() => {}); }, []);

  const canPost = ["master_admin", "super_admin", "principal", "hr_manager"].includes(user?.role);

  async function post(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/circulars", form);
      toast.success("Circular posted");
      setForm({ title: "", body: "", audience: "all" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setSaving(false); }
  }

  async function remove(c) {
    if (!window.confirm("Delete this circular?")) return;
    try { await api.delete(`/circulars/${c.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  }

  return (
    <AppShell>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-7">
        <div>
          <div className="text-xs uppercase tracking-wider font-semibold text-violet-700 mb-2">Announcements</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900">Circulars</h1>
          <p className="text-slate-500 mt-1.5">{items.length} notices currently visible to you</p>
        </div>
      </div>

        {canPost && (
              <form data-testid="circular-form" onSubmit={post} className="bg-white rounded-2xl shadow-sm p-5 mb-6 space-y-4">
                <h3 className="font-display font-bold text-slate-900">Post a circular</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>Title</Label>
                  <Input data-testid="circular-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 input-focus" required />
                </div>
                <div>
                  <Label>Audience</Label>
                  <Select value={form.audience} onValueChange={(v) => setForm({ ...form, audience: v })}>
                    <SelectTrigger data-testid="circular-audience" className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Everyone</SelectItem>
                      <SelectItem value="parent">Parents only</SelectItem>
                      <SelectItem value="staff">Staff only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                </div>
                <div>
                  <Label>Message</Label>
                  <Textarea data-testid="circular-body" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="mt-1.5" rows={4} required />
                </div>
                <Button data-testid="save-circular-btn" type="submit" disabled={saving} className="h-11 px-6 bg-violet-600 hover:bg-violet-700">
                  {saving ? <Loader2 className="animate-spin" size={18} /> : "Publish"}
                </Button>
              </form>
        )}

      {items.length === 0 ? (
        <Card className="border-dashed border-2 border-violet-200">
          <CardContent className="py-16 text-center">
            <Megaphone className="mx-auto text-violet-400" size={44} />
            <h3 className="font-display text-xl font-bold text-slate-900 mt-4">No circulars yet</h3>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {items.map((c) => (
            <Card key={c.id} data-testid={`circular-${c.id}`} className="border-violet-100">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge className="bg-violet-100 text-violet-800 hover:bg-violet-100 capitalize">{c.audience}</Badge>
                      <span className="text-xs text-slate-500">{new Date(c.posted_at).toLocaleString()}</span>
                      <span className="text-xs text-slate-500">by {c.posted_by_name}</span>
                    </div>
                    <h3 className="font-display text-lg font-bold text-slate-900">{c.title}</h3>
                    <p className="mt-2 text-sm text-slate-600 whitespace-pre-line">{c.body}</p>
                  </div>
                  {canPost && (c.posted_by === user?.id || user?.role === "master_admin") && (
                    <button data-testid={`delete-circular-${c.id}`} onClick={() => remove(c)} className="text-slate-400 hover:text-red-600 shrink-0">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </AppShell>
  );
}
